/*
=========================================================
* RingOperator Pricing — real tiers, Dispatch design
=========================================================
*/

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";

import { brandPrimary, brandCream, brandAccent, brandCard } from "pages/RingOperatorHomepage/theme";
import useSignupUrl from "hooks/useSignupUrl";
import { getDisplayTrialDays, isPromoActive, PROMO_TRIAL } from "constants/promo";

const line = "rgba(14,13,11,0.1)";
const muted = "rgba(14,13,11,0.55)";

const commonFeatures = [
  "24/7 AI Voice Agent",
  "10 premium AI voices",
  "Custom AI prompts",
  "Google Calendar integration",
  "Call transfer to human",
  "Call transcripts & recordings",
  "Customer feedback reports",
  "Holiday & special hours",
  "Free business webpage + QR code",
  "Online booking, waitlist & feedback from your page",
  "Spam call blocking",
  "Mid-call SMS links",
  "Call quality scoring",
  "Real-time analytics dashboard",
  "SMS & email notifications",
  "Customer SMS confirmations",
  "Regulatory compliance built-in",
  "30+ languages",
  "1 phone number",
  "Email support",
];

const tiers = [
  {
    name: "Starter",
    price: 25,
    minutes: "100",
    overage: "$0.25 / min",
    best: "Solo operators & side businesses",
    features: [...commonFeatures, "SMS at $0.20 each"],
    cta: "Start free",
    highlight: false,
  },
  {
    name: "Growth",
    price: 100,
    minutes: "500",
    overage: "$0.20 / min",
    best: "Busy SMBs — most popular",
    features: [
      ...commonFeatures,
      "Automated cancellation notification calls",
      "AI waitlist offer calls",
      "Follow-up call campaigns",
      "SMS at $0.15 each",
    ],
    cta: "Start free",
    highlight: true,
  },
  {
    name: "Scale",
    price: 300,
    minutes: "2000",
    overage: "$0.15 / min",
    best: "High-volume teams",
    features: [
      ...commonFeatures,
      "Automated cancellation notification calls",
      "AI waitlist offer calls",
      "Follow-up call campaigns",
      "SMS at $0.10 each",
    ],
    cta: "Start free",
    highlight: false,
  },
];

function Pricing() {
  const signupUrl = useSignupUrl();

  return (
    <MKBox
      component="section"
      id="pricing"
      className="ro-root"
      sx={{ py: { xs: 8, md: 12 }, background: brandCream }}
    >
      <Container>
        <Grid container spacing={5} alignItems="end" sx={{ mb: 6 }}>
          <Grid item xs={12} md={7}>
            <MKBox
              className="ro-mono"
              sx={{ fontSize: 12, color: muted, textTransform: "uppercase", letterSpacing: "0.12em", mb: 2 }}
            >
              ◦ Pricing
            </MKBox>
            <MKTypography
              component="h2"
              sx={{
                m: 0,
                fontSize: { xs: "2.2rem", md: "4.5rem" },
                lineHeight: 0.95,
                letterSpacing: "-0.03em",
                fontWeight: 800,
                color: brandPrimary,
              }}
            >
              Pennies per call.{" "}
              <MKBox component="span" className="ro-serif" sx={{ fontStyle: "italic" }}>
                Never a missed one.
              </MKBox>
            </MKTypography>
          </Grid>
          <Grid item xs={12} md={5}>
            <MKTypography sx={{ color: muted, fontSize: "1.05rem", lineHeight: 1.55, maxWidth: 420 }}>
              {isPromoActive() && `${PROMO_TRIAL.name}: `}Every plan includes a{" "}
              {getDisplayTrialDays()}-day free trial. No contract, no setup fee. Keep your number.
            </MKTypography>
          </Grid>
        </Grid>

        <Grid container spacing={2.5}>
          {tiers.map((t, i) => (
            <Grid item xs={12} md={4} key={i}>
              <MKBox
                sx={{
                  background: t.highlight ? brandPrimary : brandCard,
                  color: t.highlight ? brandCream : brandPrimary,
                  borderRadius: 3,
                  border: t.highlight ? "none" : `1px solid ${line}`,
                  p: { xs: 3.5, md: 4 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  boxShadow: t.highlight ? "0 20px 40px rgba(14,13,11,0.12)" : "none",
                }}
              >
                {t.highlight && (
                  <MKBox
                    sx={{
                      position: "absolute",
                      top: 18,
                      right: 18,
                      background: brandAccent,
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 999,
                    }}
                  >
                    Most popular
                  </MKBox>
                )}

                <MKTypography
                  component="h3"
                  sx={{
                    m: 0,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                    color: "inherit",
                  }}
                >
                  {t.name}
                </MKTypography>
                <MKTypography
                  sx={{
                    fontSize: "0.9rem",
                    color: t.highlight ? "rgba(244,241,234,0.65)" : muted,
                    mt: 0.5,
                  }}
                >
                  {t.best}
                </MKTypography>

                <MKBox sx={{ mt: 3, display: "flex", alignItems: "baseline", gap: 0.5 }}>
                  <MKTypography
                    sx={{
                      fontSize: "3.5rem",
                      fontWeight: 800,
                      letterSpacing: "-0.04em",
                      lineHeight: 1,
                      color: "inherit",
                    }}
                  >
                    ${t.price}
                  </MKTypography>
                  <MKTypography
                    sx={{ fontSize: "0.95rem", color: t.highlight ? "rgba(244,241,234,0.65)" : muted }}
                  >
                    /mo
                  </MKTypography>
                </MKBox>

                <MKBox
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                    color: t.highlight ? "rgba(244,241,234,0.75)" : muted,
                    borderTop: `1px solid ${t.highlight ? "rgba(244,241,234,0.12)" : line}`,
                    borderBottom: `1px solid ${t.highlight ? "rgba(244,241,234,0.12)" : line}`,
                    py: 1.5,
                  }}
                >
                  <span>
                    <MKBox
                      component="span"
                      sx={{ color: t.highlight ? brandCream : brandPrimary, fontWeight: 600 }}
                    >
                      {t.minutes}
                    </MKBox>{" "}
                    min included
                  </span>
                  <span>
                    then{" "}
                    <MKBox
                      component="span"
                      sx={{ color: t.highlight ? brandCream : brandPrimary, fontWeight: 600 }}
                    >
                      {t.overage}
                    </MKBox>
                  </span>
                </MKBox>

                <MKBox component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: 2.5, flex: 1 }}>
                  {t.features.map((f, j) => (
                    <MKBox
                      component="li"
                      key={j}
                      sx={{
                        fontSize: "0.95rem",
                        lineHeight: 1.5,
                        py: 0.75,
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                        color: t.highlight ? "rgba(244,241,234,0.85)" : brandPrimary,
                      }}
                    >
                      <MKBox
                        component="span"
                        sx={{
                          mt: "2px",
                          color: brandAccent,
                          fontWeight: 700,
                          fontSize: 14,
                        }}
                      >
                        ✓
                      </MKBox>
                      {f}
                    </MKBox>
                  ))}
                </MKBox>

                <MKButton
                  fullWidth
                  onClick={() => (window.location.href = signupUrl)}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    background: `${t.highlight ? brandAccent : brandPrimary} !important`,
                    color: "#fff !important",
                    "&:hover": { background: `${t.highlight ? "#2a47c4" : "#1a1915"} !important` },
                  }}
                >
                  {t.cta} →
                </MKButton>
              </MKBox>
            </Grid>
          ))}
        </Grid>

        <MKTypography
          className="ro-mono"
          sx={{
            textAlign: "center",
            fontSize: 12,
            color: muted,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            mt: 5,
          }}
        >
          {getDisplayTrialDays()}-day free trial · no contract · cancel anytime
        </MKTypography>
      </Container>
    </MKBox>
  );
}

export default Pricing;
